import { TimeSeriesFetcher } from "~/modules/visualization/components/Timeseries/TimeSeriesFetcher";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3";
import { ApiQueryEndpoints, Filter } from "~/modules/filters/types";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { ColumnSpanConfig } from "~/types";
import { useState, type ReactNode } from "react";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import { FadeIn } from "~/components/FadeIn";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { ExportTableNames } from "~/modules/downloads/types";
import { getAverageSeries } from "./getAverageSeries";
import { getTimeSeriesViews } from "./getTimeSeriesViews";
import { MetricTimeseriesControls } from "./MetricTimeseriesControls";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import type { TimeSeriesItem } from "~/modules/asset_timeseries_v4/types";
import { RWA_COLORS } from "~/styles/colors";
import { VisualizationDropdown } from "../VisualizationDropdown";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { VisualizationApiToolsButton } from "../VisualizationApiToolsButton";
import { ButtonGroup } from "~/components/ButtonGroup";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { RenderWhenVisible } from "~/components/RenderWhenVisible";

// // // //

export enum MetricTypes {
    Hypothetical10KPerformance = "hypothetical_10k_performance",
    Apy7Day = "apy_7_day",
    Apy30Day = "apy_30_day",
}

const METRICS_DROPDOWN_OPTIONS = [
    {
        label: "Hypothetical $10K Performance",
        value: MetricTypes.Hypothetical10KPerformance,
    },
    { label: "APY 7-Day", value: MetricTypes.Apy7Day },
    { label: "APY 30-Day", value: MetricTypes.Apy30Day },
];

// // // //

interface LegendProps {
    enabled: boolean;
    active: boolean;
    align?: "left" | "right" | "center";
    layout?: "vertical" | "horizontal";
    verticalAlign?: "top" | "bottom";
    itemWidth?: number;
    maxHeight?: number;
}

interface NavigatorProps {
    enabled: boolean;
    active: boolean;
}

interface MetricsProps {
    options: MetricTypes[];
}

// // // //

const DEFAULT_LEGEND_PROPS: LegendProps = {
    enabled: true,
    active: true,
    align: "right",
    layout: "vertical",
    verticalAlign: "top",
    itemWidth: 120,
};

const DEFAULT_NAVIGATOR_PROPS: NavigatorProps = {
    enabled: true,
    active: true,
};

const DEFAULT_METRICS_PROPS: MetricsProps = {
    options: [
        MetricTypes.Hypothetical10KPerformance,
        MetricTypes.Apy7Day,
        MetricTypes.Apy30Day,
    ],
};

// // // //

interface Props {
    title?: string;
    subtitle?: string;
    infoTooltip?: ReactNode;
    footer?: ReactNode;
    headerControls?: ReactNode;
    headerControlsPosition?: "header" | "controlBar";
    additionalSeries?: TimeSeriesItem[];
    dataSeriesName?: string;
    dataSeriesNamesByGroupID?: Record<number, string>;
    dataSeriesColor?: string;
    displayStartDate?: string;
    trailingAverageDays?: number;
    valueRange?: {
        min?: number;
        max?: number;
    };
    yAxisMin?: number;
    lineWidth?: number;
    hideLegend?: boolean;
    showAverageOnly?: boolean;
    showAverageWithConstituents?: boolean;
    showControls?: boolean;
    columnSpanConfig?: ColumnSpanConfig;
    height?: number;
    groupBy?: AssetTimeseriesGroupBy;
    filters?: Filter[];
    controls?: {
        navigator?: NavigatorProps;
        legend?: LegendProps;
        metrics?: MetricsProps;
    };
    analyticsChartName: string;
}

/**
 * YieldTimeseriesChart
 * Displays yield performance over time for assets
 */
export function YieldTimeseriesChart(props: Props) {
    const {
        title = "Yield Performance",
        subtitle,
        infoTooltip,
        footer,
        headerControls,
        headerControlsPosition = "header",
        additionalSeries = [],
        dataSeriesName,
        dataSeriesNamesByGroupID,
        dataSeriesColor,
        displayStartDate,
        trailingAverageDays,
        valueRange,
        yAxisMin,
        lineWidth,
        hideLegend = false,
        showAverageOnly = false,
        showAverageWithConstituents = false,
        showControls = true,
        height = 350,
        groupBy = AssetTimeseriesGroupBy.assetClass,
        filters = [],
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
        controls,
        analyticsChartName,
    } = props;

    const metrics = controls?.metrics
        ? { ...DEFAULT_METRICS_PROPS, ...controls.metrics }
        : DEFAULT_METRICS_PROPS;

    const legend = controls?.legend
        ? { ...DEFAULT_LEGEND_PROPS, ...controls.legend }
        : DEFAULT_LEGEND_PROPS;

    const navigator = controls?.navigator
        ? { ...DEFAULT_NAVIGATOR_PROPS, ...controls.navigator }
        : DEFAULT_NAVIGATOR_PROPS;

    const [showNavigator, setShowNavigator] = useState(navigator.active);
    const [showLegend, setShowLegend] = useState(legend.active);

    const supportedMetrics = METRICS_DROPDOWN_OPTIONS.filter((option) =>
        metrics.options.includes(option.value as MetricTypes),
    );

    return (
        <MetricTimeseriesControls
            groupBy={groupBy}
            filters={filters}
            metrics={metrics}
            analyticsChartName={analyticsChartName}
        >
            {({ appliedQuery, metric, ignoreDownloadsFeatureFlag }) => (
                <TimeSeriesFetcher
                    endpoint={ApiQueryEndpoints.AssetTimeseriesV4}
                    query={appliedQuery}
                >
                    {({ data, loading }) => {
                        const namedData = dataSeriesNamesByGroupID
                            ? data.map((item) => ({
                                  ...item,
                                  group: {
                                      ...item.group,
                                      name:
                                          dataSeriesNamesByGroupID[
                                              item.group.id
                                          ] ?? item.group.name,
                                  },
                              }))
                            : data;
                        const filteredData = valueRange
                            ? namedData.map((item) => ({
                                  ...item,
                                  points: item.points.filter(([, value]) => {
                                      const numericValue = Number(value);

                                      return (
                                          Number.isFinite(numericValue) &&
                                          (valueRange.min === undefined ||
                                              numericValue >= valueRange.min) &&
                                          (valueRange.max === undefined ||
                                              numericValue <= valueRange.max)
                                      );
                                  }),
                              }))
                            : namedData;
                        const series =
                            showAverageOnly || showAverageWithConstituents
                                ? [
                                      ...(showAverageWithConstituents
                                          ? getTimeSeriesViews({
                                                data: filteredData.map(
                                                    (item) => ({
                                                        ...item,
                                                        group: {
                                                            ...item.group,
                                                            color: RWA_COLORS
                                                                .slate[400],
                                                        },
                                                    }),
                                                ),
                                                displayStartDate,
                                            }).map((view) => ({
                                                ...view,
                                                lineWidth: 1,
                                                opacity: 0.4,
                                                showInLegend: false,
                                                zIndex: 1,
                                            }))
                                          : []),
                                      ...getTimeSeriesViews({
                                          data: [
                                              getAverageSeries(
                                                  filteredData,
                                                  dataSeriesName ?? "Average",
                                                  dataSeriesColor ??
                                                      RWA_COLORS.blue[600],
                                              ),
                                          ].filter(
                                              (item): item is TimeSeriesItem =>
                                                  item !== null,
                                          ),
                                          displayStartDate,
                                          trailingAverageDays,
                                      }).map((view) => ({
                                          ...view,
                                          lineWidth,
                                          opacity: 1,
                                          showInLegend: true,
                                          zIndex: 3,
                                      })),
                                      ...getTimeSeriesViews({
                                          data: additionalSeries,
                                          displayStartDate,
                                          trailingAverageDays,
                                      }),
                                  ]
                                : getTimeSeriesViews({
                                      data: [
                                          ...filteredData.map((item) => ({
                                              ...item,
                                              group: {
                                                  ...item.group,
                                                  name:
                                                      dataSeriesName ??
                                                      item.group.name,
                                                  color:
                                                      dataSeriesColor ??
                                                      item.group.color,
                                              },
                                          })),
                                          ...additionalSeries,
                                      ],
                                      displayStartDate,
                                      trailingAverageDays,
                                  });
                        const chartInstanceKey = JSON.stringify(
                            series.map((view) => ({
                                groupID: view.item.group.id,
                                groupType: view.item.group.type,
                                name: view.item.group.name,
                            })),
                        );

                        return (
                            <DataExportProvider
                                ignoreFeatureFlag={ignoreDownloadsFeatureFlag}
                            >
                                {({
                                    isLoading: isDownloadLoading,
                                    exportCsv,
                                }) => {
                                    const defaultControls = showControls ? (
                                        <div className="flex items-center gap-3">
                                            <VisualizationApiToolsButton
                                                query={appliedQuery}
                                                apiDocsBaseUrl={
                                                    "/v4/assets/aggregates/timeseries"
                                                }
                                            />

                                            <DownloadButton
                                                testID="visualization-controls--download-button"
                                                disabled={isDownloadLoading}
                                                onClick={() => {
                                                    exportCsv({
                                                        query: appliedQuery,
                                                        exportTableName:
                                                            ExportTableNames.asset_timeseries_v4,
                                                    });
                                                }}
                                            />

                                            <VisualizationDropdown
                                                navigator={
                                                    navigator.enabled
                                                        ? {
                                                              value: showNavigator,
                                                              onChange: () => {
                                                                  setShowNavigator(
                                                                      !showNavigator,
                                                                  );
                                                              },
                                                          }
                                                        : undefined
                                                }
                                                legend={
                                                    legend.enabled
                                                        ? {
                                                              value: showLegend,
                                                              toggle: () => {
                                                                  setShowLegend(
                                                                      !showLegend,
                                                                  );
                                                              },
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    ) : undefined;

                                    const customControlBar =
                                        headerControlsPosition ===
                                            "controlBar" ||
                                        supportedMetrics.length > 1 ? (
                                            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-start gap-3 xl:gap-6 mb-2 mt-1">
                                                {headerControlsPosition ===
                                                    "controlBar" &&
                                                    headerControls}
                                                {supportedMetrics.length >
                                                    1 && (
                                                    <ButtonGroup
                                                        title="Metric"
                                                        options={
                                                            supportedMetrics
                                                        }
                                                        value={metric.value}
                                                        disabled={loading}
                                                        onValueChange={(v) =>
                                                            metric.onChange(
                                                                v as MetricTypes,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        ) : null;

                                    return (
                                        <VisualizationCard
                                            colSpans={columnSpanConfig}
                                            title={title}
                                            subtitle={subtitle}
                                            infoTooltip={infoTooltip}
                                            footer={footer}
                                            wrapperClassNameOverride=""
                                            watermark={
                                                loading || data.length === 0
                                                    ? "none"
                                                    : "center-30pct"
                                            }
                                            showTimestamp={false}
                                            customControls={
                                                headerControlsPosition ===
                                                "header"
                                                    ? (headerControls ??
                                                      defaultControls)
                                                    : defaultControls
                                            }
                                            customControlBar={customControlBar}
                                        >
                                            {loading && (
                                                <FadeIn>
                                                    <div
                                                        className="flex items-center justify-center w-full"
                                                        style={{
                                                            height: `${height}px`,
                                                        }}
                                                    >
                                                        <LoadingSpinner
                                                            size={10}
                                                        />
                                                    </div>
                                                </FadeIn>
                                            )}

                                            {!loading && (
                                                <WindowResizeObserver
                                                    delay={800}
                                                    defaultPlaceholderHeight={
                                                        height
                                                    }
                                                >
                                                    <RenderWhenVisible
                                                        placeholderHeight={
                                                            height
                                                        }
                                                    >
                                                        <TimeSeriesChart
                                                            key={
                                                                chartInstanceKey
                                                            }
                                                            series={series}
                                                            area={false}
                                                            showTotal={false}
                                                            height={height}
                                                            lineWidth={
                                                                lineWidth
                                                            }
                                                            maxTooltipEntries={
                                                                20
                                                            }
                                                            yAxis={{
                                                                min: yAxisMin,
                                                                enabled: true,
                                                            }}
                                                            navigator={{
                                                                enabled:
                                                                    navigator.active,
                                                                active: showNavigator,
                                                                forceUpdate: true,
                                                            }}
                                                            legend={{
                                                                ...legend,
                                                                enabled:
                                                                    !hideLegend &&
                                                                    showLegend,
                                                            }}
                                                        />
                                                    </RenderWhenVisible>
                                                </WindowResizeObserver>
                                            )}
                                        </VisualizationCard>
                                    );
                                }}
                            </DataExportProvider>
                        );
                    }}
                </TimeSeriesFetcher>
            )}
        </MetricTimeseriesControls>
    );
}
